// src/components/documents/useCatalogSearch.js — doced-8f3a61c2 (Document editor, Stage 4)
//
// Catalog searching for the estimate page's quick add and "Add from catalog" panel. Both ask the
// server (searchCatalogPicker, catpick-4d7b19e3 Stage 1, live): the browser never loads the whole
// catalog; archived items never appear; cost and markup come back ONLY for Owner / Admin / Manager
// (for everyone else the keys aren't in the answer at all).
//
// Folder NAMES are read live from catalogFolders (open to every staff member since catpick Stage 1),
// for the panel's folder chips and the "· Breakers" hint beside a result.

import { useEffect, useMemo, useRef, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import { listenFolders, friendlyError } from '../settings/catalog/catalogApi';
import { folderTree, folderPath } from '../../utils/catalogUi';

const pickerCall = () => httpsCallable(functions, 'searchCatalogPicker');
export const SEARCH_FAILED = 'Couldn’t search the catalog. Please try again.';

/** The company's live folders → { tree, pathOf(item) } ("Electrical › Breakers"). */
export function useCatalogFolders(orgId) {
    const [folders, setFolders] = useState([]);
    const [error, setError] = useState('');
    useEffect(() => {
        if (!orgId) return undefined;
        return listenFolders(orgId, (list) => { setFolders(list); setError(''); }, (e) => { console.warn('doced: folders listener failed', e); setError('Folders couldn’t be loaded.'); });
    }, [orgId]);
    return useMemo(() => {
        const tree = folderTree(folders);
        const pathOf = (item) => {
            const ids = Array.isArray(item?.folderIds) ? item.folderIds : [];
            const first = ids.find((id) => tree.byId.has(id));
            return first ? folderPath(tree.byId, first) : '';
        };
        return { tree, pathOf, error };
    }, [folders, error]);
}

/**
 * Quick add: a few results for what's being typed, after a pause (debounced). Results for an older
 * query never replace a newer one. → { status: 'idle'|'loading'|'done'|'error', items, error }
 */
export function useQuickCatalogSearch(text, { minChars = 2, delayMs = 300, max = 5 } = {}) {
    const [state, setState] = useState({ status: 'idle', items: [], error: '' });
    const seq = useRef(0);
    const q = (text || '').trim();
    useEffect(() => {
        const mine = ++seq.current;
        if (q.length < minChars) { setState({ status: 'idle', items: [], error: '' }); return undefined; }
        setState((s) => ({ ...s, status: 'loading', error: '' }));
        const t = setTimeout(async () => {
            try {
                const res = await pickerCall()({ query: q, quick: true, pageSize: max });
                if (mine !== seq.current) return;
                setState({ status: 'done', items: (res.data?.items || []).slice(0, max), error: '' });
            } catch (e) {
                if (mine !== seq.current) return;
                console.warn('doced: quick add search failed', e);
                setState({ status: 'error', items: [], error: friendlyError(e, SEARCH_FAILED) });
            }
        }, delayMs);
        return () => clearTimeout(t);
    }, [q, minChars, delayMs, max]);
    return state;
}

/**
 * The panel's search: words, a folder or Favourites, a page at a time ("Show more").
 * filters = { query, folderId, favoritesOnly }. → { status, items, hasMore, loadMore, error, retry }
 */
export function usePanelCatalogSearch(filters, { enabled, delayMs = 300, pageSize = 30 } = {}) {
    const [state, setState] = useState({ status: 'idle', items: [], cursor: null, error: '' });
    const seq = useRef(0);
    const [nonce, setNonce] = useState(0);
    const key = JSON.stringify([(filters.query || '').trim(), filters.folderId || '', !!filters.favoritesOnly]);
    const input = useMemo(() => {
        const [query, folderId, fav] = JSON.parse(key);
        const body = { pageSize };
        if (query) body.query = query;
        if (folderId) body.folderId = folderId;
        if (fav) body.favoritesOnly = true;
        return body;
    }, [key, pageSize]);

    useEffect(() => {
        if (!enabled) return undefined;
        const mine = ++seq.current;
        setState({ status: 'loading', items: [], cursor: null, error: '' });
        const t = setTimeout(async () => {
            try {
                const res = await pickerCall()(input);
                if (mine !== seq.current) return;
                setState({ status: 'done', items: res.data?.items || [], cursor: res.data?.nextCursor || null, error: '' });
            } catch (e) {
                if (mine !== seq.current) return;
                console.warn('doced: catalog panel search failed', e);
                setState({ status: 'error', items: [], cursor: null, error: friendlyError(e, SEARCH_FAILED) });
            }
        }, input.query ? delayMs : 0);
        return () => clearTimeout(t);
    }, [input, enabled, delayMs, nonce]);

    const loadMore = async () => {
        if (!state.cursor || state.status === 'loadingMore') return;
        const mine = seq.current;
        const cursor = state.cursor;
        setState((s) => ({ ...s, status: 'loadingMore', error: '' }));
        try {
            const res = await pickerCall()({ ...input, cursor });
            if (mine !== seq.current) return;
            setState((s) => ({ status: 'done', items: [...s.items, ...(res.data?.items || [])], cursor: res.data?.nextCursor || null, error: '' }));
        } catch (e) {
            if (mine !== seq.current) return;
            setState((s) => ({ ...s, status: 'done', error: friendlyError(e, SEARCH_FAILED) }));
        }
    };
    return { ...state, hasMore: !!state.cursor, loadMore, retry: () => setNonce((n) => n + 1) };
}
